import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch } from '../constants';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';

const API = `${API_V1}/users`;

const ROLES = ['ADMIN', 'CFO', 'CONTROLLER', 'REVIEWER', 'AUDITOR', 'VIEWER'] as const;
const DOA_FROM_ROLE: Record<string, string> = {
  ADMIN: 'L7', CFO: 'L6', CONTROLLER: 'L4', REVIEWER: 'L3', AUDITOR: 'L3', VIEWER: 'L2',
};

interface User {
  id: number;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  is_admin: boolean;
  department: string | null;
  approval_limit: number;
  last_login: string | null;
  created_at: string | null;
}

export default function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [resetUser, setResetUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const [createForm, setCreateForm] = useState({
    email: '', full_name: '', password: '', role: 'VIEWER', department: '', approval_limit: '0',
  });
  const [editForm, setEditForm] = useState({
    full_name: '', role: '', department: '', approval_limit: '',
  });

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(API);
      if (res.ok) setUsers(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    try {
      const payload = { ...createForm, approval_limit: parseInt(createForm.approval_limit) || 0 };
      const res = await authFetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) {
        showToast('User created', true);
        setShowCreate(false);
        setCreateForm({ email: '', full_name: '', password: '', role: 'VIEWER', department: '', approval_limit: '0' });
        load();
      } else {
        const e = await res.json();
        showToast(e.detail || 'Error creating user', false);
      }
    } catch { showToast('Failed to create user', false); }
  };

  const handleEdit = async () => {
    if (!editUser) return;
    try {
      const payload: any = {};
      if (editForm.full_name) payload.full_name = editForm.full_name;
      if (editForm.role) payload.role = editForm.role;
      if (editForm.department !== undefined) payload.department = editForm.department;
      if (editForm.approval_limit) payload.approval_limit = parseInt(editForm.approval_limit) || 0;
      const res = await authFetch(`${API}/${editUser.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) {
        showToast('User updated', true);
        setEditUser(null);
        load();
      } else {
        const e = await res.json();
        showToast(e.detail || 'Error', false);
      }
    } catch { showToast('Update failed', false); }
  };

  const handleResetPassword = async () => {
    if (!resetUser || !newPassword) return;
    try {
      const res = await authFetch(`${API}/${resetUser.id}/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: newPassword }),
      });
      if (res.ok) { showToast('Password reset', true); setResetUser(null); setNewPassword(''); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Reset failed', false); }
  };

  const handleDeactivate = async (user: User) => {
    if (!window.confirm(`Deactivate ${user.full_name}?`)) return;
    try {
      const res = await authFetch(`${API}/${user.id}/deactivate`, { method: 'POST' });
      if (res.ok) { showToast('User deactivated', true); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Failed', false); }
  };

  const openEdit = (u: User) => {
    setEditUser(u);
    setEditForm({ full_name: u.full_name, role: u.role, department: u.department || '', approval_limit: String(u.approval_limit || 0) });
  };

  // KPI computations
  const total = users.length;
  const active = users.filter(u => u.is_active).length;
  const admins = users.filter(u => u.role === 'ADMIN').length;
  const byRole: Record<string, number> = {};
  users.forEach(u => { byRole[u.role] = (byRole[u.role] || 0) + 1; });

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };
  const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', marginBottom: 4, display: 'block' };

  const userColumns: Column[] = [
    { key: 'full_name', label: 'Name', render: (v: any) => <span style={{ fontWeight: 500 }}>{v}</span> },
    { key: 'email', label: 'Email', render: (v: any) => <span style={{ color: 'var(--text-secondary)' }}>{v}</span> },
    { key: 'role', label: 'Role', render: (v: any) => (
      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: v === 'ADMIN' ? 'var(--accent)' : 'var(--bg-elevated)', color: v === 'ADMIN' ? '#fff' : 'var(--text-primary)' }}>{v}</span>
    )},
    { key: 'doa', label: 'DOA Level', render: (_: any, row: User) => <span style={{ color: 'var(--text-secondary)' }}>{DOA_FROM_ROLE[row.role] || 'L2'}</span> },
    { key: 'department', label: 'Department', render: (v: any) => <span style={{ color: 'var(--text-secondary)' }}>{v || '-'}</span> },
    { key: 'is_active', label: 'Status', render: (v: any) => (
      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: v ? '#dcfce7' : '#fee2e2', color: v ? '#166534' : '#991b1b' }}>
        {v ? 'Active' : 'Inactive'}
      </span>
    )},
    { key: 'last_login', label: 'Last Login', render: (v: any) => <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{v ? new Date(v).toLocaleDateString() : 'Never'}</span> },
    { key: 'actions', label: 'Actions', render: (_: any, row: User) => (
      <div style={{ display: 'flex', gap: 4 }}>
        <ActionButton label="Edit" onClick={() => openEdit(row)} variant="secondary" size="sm" />
        <ActionButton label="Reset PW" onClick={() => { setResetUser(row); setNewPassword(''); }} variant="secondary" size="sm" />
        {row.is_active && (
          <ActionButton label="Deactivate" onClick={() => handleDeactivate(row)} variant="danger" size="sm" />
        )}
      </div>
    )},
  ];

  return (
    <PageLayout
      title="User Management"
      subtitle="Manage users, roles, and access controls"
      action={{ label: '+ New User', onClick: () => setShowCreate(true) }}
    >
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 1000, padding: '10px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive)' : 'var(--negative)', color: '#fff', fontSize: 13, fontWeight: 600 }}>
          {toast.msg}
        </div>
      )}

      <KpiGrid items={[
        { label: 'Total Users', value: total },
        { label: 'Active', value: active, color: 'var(--positive)' },
        { label: 'Admins', value: admins, color: 'var(--accent)' },
        ...Object.entries(byRole).slice(0, 4).map(([role, count]) => ({
          label: role, value: count, color: 'var(--text-secondary)',
        })),
      ]} />

      <DataTable columns={userColumns} rows={users} loading={loading} rowKey="id" emptyMessage="No users found" />

      {/* Create User Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create User" size="md"
        footer={
          <>
            <ActionButton label="Cancel" onClick={() => setShowCreate(false)} variant="secondary" />
            <ActionButton label="Create" onClick={handleCreate} />
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div><label style={labelStyle}>Email</label><input style={inputStyle} value={createForm.email} onChange={e => setCreateForm({ ...createForm, email: e.target.value })} /></div>
          <div><label style={labelStyle}>Full Name</label><input style={inputStyle} value={createForm.full_name} onChange={e => setCreateForm({ ...createForm, full_name: e.target.value })} /></div>
          <div><label style={labelStyle}>Password</label><input type="password" style={inputStyle} value={createForm.password} onChange={e => setCreateForm({ ...createForm, password: e.target.value })} /></div>
          <div>
            <label style={labelStyle}>Role</label>
            <select style={inputStyle} value={createForm.role} onChange={e => setCreateForm({ ...createForm, role: e.target.value })}>
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div><label style={labelStyle}>Department</label><input style={inputStyle} value={createForm.department} onChange={e => setCreateForm({ ...createForm, department: e.target.value })} /></div>
          <div><label style={labelStyle}>Approval Limit ($)</label><input type="number" style={inputStyle} value={createForm.approval_limit} onChange={e => setCreateForm({ ...createForm, approval_limit: e.target.value })} /></div>
          <div style={{ fontSize: 11, color: 'var(--text-hint)', padding: '4px 0' }}>
            DOA Level: <strong>{DOA_FROM_ROLE[createForm.role] || 'L2'}</strong> (auto-set from role)
          </div>
        </div>
      </Modal>

      {/* Edit User Modal */}
      <Modal open={!!editUser} onClose={() => setEditUser(null)} title={`Edit User: ${editUser?.full_name || ''}`} size="md"
        footer={
          <>
            <ActionButton label="Cancel" onClick={() => setEditUser(null)} variant="secondary" />
            <ActionButton label="Save" onClick={handleEdit} />
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div><label style={labelStyle}>Full Name</label><input style={inputStyle} value={editForm.full_name} onChange={e => setEditForm({ ...editForm, full_name: e.target.value })} /></div>
          <div>
            <label style={labelStyle}>Role</label>
            <select style={inputStyle} value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })}>
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div><label style={labelStyle}>Department</label><input style={inputStyle} value={editForm.department} onChange={e => setEditForm({ ...editForm, department: e.target.value })} /></div>
          <div><label style={labelStyle}>Approval Limit ($)</label><input type="number" style={inputStyle} value={editForm.approval_limit} onChange={e => setEditForm({ ...editForm, approval_limit: e.target.value })} /></div>
          <div style={{ fontSize: 11, color: 'var(--text-hint)', padding: '4px 0' }}>
            DOA Level: <strong>{DOA_FROM_ROLE[editForm.role] || 'L2'}</strong> (auto-set from role)
          </div>
        </div>
      </Modal>

      {/* Reset Password Modal */}
      <Modal open={!!resetUser} onClose={() => setResetUser(null)} title={`Reset Password: ${resetUser?.full_name || ''}`} size="sm"
        footer={
          <>
            <ActionButton label="Cancel" onClick={() => setResetUser(null)} variant="secondary" />
            <ActionButton label="Reset Password" onClick={handleResetPassword} disabled={!newPassword} />
          </>
        }
      >
        <div>
          <label style={labelStyle}>New Password</label>
          <input type="password" style={inputStyle} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password" />
        </div>
      </Modal>
    </PageLayout>
  );
}
